/**
 * Cleanup Coordinator
 * Prevents race conditions and conflicts between multiple cleanup operations
 * by implementing a centralized coordination system with mutex locks.
 */

/**
 * @typedef {Object} CleanupOperation
 * @property {string} id - Unique operation ID
 * @property {string} type - Operation type (broker_cleanup, engagement_cleanup, etc.)
 * @property {string} scope - Operation scope (user_specific, global, etc.)
 * @property {string} [userId] - User ID for user-specific operations
 * @property {Date} startTime - Operation start time
 * @property {Date} [endTime] - Operation end time
 * @property {string} status - Operation status (pending, running, completed, failed)
 * @property {Object} [result] - Operation result
 * @property {string} [error] - Error message if failed
 */

/**
 * Cleanup operation types
 */
export const CLEANUP_TYPES = {
  BROKER_INTEGRATIONS: 'broker_integrations',
  ENGAGEMENT_DATA: 'engagement_data',
  DISCONNECTED_BROKERS: 'disconnected_brokers',
  USER_DATA: 'user_data',
  CACHE_CLEANUP: 'cache_cleanup'
};

/**
 * Cleanup operation scopes
 */
export const CLEANUP_SCOPES = {
  USER_SPECIFIC: 'user_specific',
  BROKER_SPECIFIC: 'broker_specific',
  GLOBAL: 'global'
};

/**
 * In-memory tracking of active cleanup operations
 * Using Map for O(1) lookup performance
 */
class CleanupCoordinator {
  constructor() {
    // Track active operations
    this.activeOperations = new Map();

    // Track operation locks
    this.locks = new Map();

    // Cleanup history (last 100 operations)
    this.operationHistory = [];
    this.maxHistorySize = 100;

    // Cleanup timeouts and intervals
    this.cleanupTimeouts = new Map();

    // Warning thresholds
    this.maxConcurrentOperations = 5;
    this.maxOperationDuration = 5 * 60 * 1000; // 5 minutes
  }

  /**
   * Generate unique operation ID
   */
  generateOperationId(type, scope) {
    const timestamp = Date.now();
    const random = Math.random().toString(36).substr(2, 9);
    return `${type}_${scope}_${timestamp}_${random}`;
  }

  /**
   * Get lock key for operation coordination
   */
  getLockKey(type, scope, userId = null) {
    if (scope === CLEANUP_SCOPES.USER_SPECIFIC && userId) {
      return `${type}:${scope}:${userId}`;
    }
    return `${type}:${scope}`;
  }

  /**
   * Check if operation is allowed to proceed
   */
  canProceed(type, scope, userId = null) {
    const lockKey = this.getLockKey(type, scope, userId);

    // Check if there's an active lock for this operation type/scope
    if (this.locks.has(lockKey)) {
      const lockInfo = this.locks.get(lockKey);
      console.warn(`🔒 Operation blocked: ${type} for ${scope} is already running (started: ${lockInfo.startTime})`);
      return false;
    }

    // Check for conflicting operations
    const conflicts = this.getConflictingOperations(type, scope, userId);
    if (conflicts.length > 0) {
      console.warn(`⚠️ Operation blocked: ${conflicts.length} conflicting operations detected`);
      conflicts.forEach(op => {
        console.warn(`  - ${op.type} (${op.scope}) started at ${op.startTime}`);
      });
      return false;
    }

    // Check if we're at max concurrent operations
    if (this.activeOperations.size >= this.maxConcurrentOperations) {
      console.warn(`🚫 Operation blocked: Maximum concurrent operations (${this.maxConcurrentOperations}) reached`);
      return false;
    }

    return true;
  }

  /**
   * Get operations that would conflict with the proposed operation
   */
  getConflictingOperations(type, scope, userId = null) {
    const conflicts = [];

    for (const [opId, operation] of this.activeOperations) {
      // Same type operations always conflict
      if (operation.type === type) {
        conflicts.push(operation);
        continue;
      }

      // User-specific operations conflict with user data operations
      if (scope === CLEANUP_SCOPES.USER_SPECIFIC && userId) {
        if (operation.scope === CLEANUP_SCOPES.USER_SPECIFIC && operation.userId === userId) {
          conflicts.push(operation);
          continue;
        }
        // User operations conflict with global operations on sensitive data
        if (operation.scope === CLEANUP_SCOPES.GLOBAL &&
            (operation.type === CLEANUP_TYPES.USER_DATA || operation.type === CLEANUP_TYPES.ENGAGEMENT_DATA)) {
          conflicts.push(operation);
          continue;
        }
      }

      // Global operations conflict with all user-specific operations if they're data-sensitive
      if (scope === CLEANUP_SCOPES.GLOBAL &&
          (type === CLEANUP_TYPES.USER_DATA || type === CLEANUP_TYPES.ENGAGEMENT_DATA)) {
        if (operation.scope === CLEANUP_SCOPES.USER_SPECIFIC) {
          conflicts.push(operation);
          continue;
        }
      }

      // Broker cleanup operations have special conflicts
      if (type === CLEANUP_TYPES.BROKER_INTEGRATIONS || type === CLEANUP_TYPES.DISCONNECTED_BROKERS) {
        if (operation.type === CLEANUP_TYPES.BROKER_INTEGRATIONS ||
            operation.type === CLEANUP_TYPES.DISCONNECTED_BROKERS ||
            operation.type === CLEANUP_TYPES.USER_DATA) {
          conflicts.push(operation);
          continue;
        }
      }
    }

    return conflicts;
  }

  /**
   * Start a cleanup operation
   */
  async startOperation(type, scope, cleanupFunction, options = {}) {
    const { userId = null, description = '', timeout = this.maxOperationDuration } = options;

    // Check if operation can proceed
    if (!this.canProceed(type, scope, userId)) {
      throw new Error(`Cleanup operation blocked: ${type} (${scope}) cannot proceed due to conflicts`);
    }

    const operationId = this.generateOperationId(type, scope);
    const lockKey = this.getLockKey(type, scope, userId);

    // Create operation record
    const operation = {
      id: operationId,
      type,
      scope,
      userId,
      description,
      startTime: new Date(),
      status: 'running'
    };

    // Acquire lock
    this.locks.set(lockKey, {
      operationId,
      startTime: operation.startTime,
      type,
      scope,
      userId
    });

    // Track active operation
    this.activeOperations.set(operationId, operation);

    console.log(`🚀 Starting cleanup operation: ${operationId} (${type})`);
    if (description) {
      console.log(`   Description: ${description}`);
    }

    // Set timeout for operation
    const timeoutId = setTimeout(() => {
      this.timeoutOperation(operationId);
    }, timeout);
    this.cleanupTimeouts.set(operationId, timeoutId);

    try {
      // Execute the cleanup function
      const result = await cleanupFunction();

      // Mark operation as completed
      operation.endTime = new Date();
      operation.status = 'completed';
      operation.result = result;

      console.log(`✅ Cleanup operation completed: ${operationId}`);
      console.log(`   Duration: ${operation.endTime - operation.startTime}ms`);
      console.log(`   Result:`, result);

      return result;

    } catch (error) {
      // Mark operation as failed
      operation.endTime = new Date();
      operation.status = 'failed';
      operation.error = error.message;

      console.error(`❌ Cleanup operation failed: ${operationId}`);
      console.error(`   Error: ${error.message}`);
      console.error(`   Duration: ${operation.endTime - operation.startTime}ms`);

      throw error;

    } finally {
      // Clean up tracking
      this.finishOperation(operationId, lockKey);
    }
  }

  /**
   * Handle operation timeout
   */
  timeoutOperation(operationId) {
    const operation = this.activeOperations.get(operationId);
    if (operation && operation.status === 'running') {
      console.error(`⏰ Cleanup operation timed out: ${operationId}`);
      operation.endTime = new Date();
      operation.status = 'failed';
      operation.error = 'Operation timed out';

      // Force cleanup
      const lockKey = this.getLockKey(operation.type, operation.scope, operation.userId);
      this.finishOperation(operationId, lockKey);
    }
  }

  /**
   * Finish operation and clean up tracking
   */
  finishOperation(operationId, lockKey) {
    // Clear timeout
    const timeoutId = this.cleanupTimeouts.get(operationId);
    if (timeoutId) {
      clearTimeout(timeoutId);
      this.cleanupTimeouts.delete(operationId);
    }

    // Release lock
    this.locks.delete(lockKey);

    // Move to history
    const operation = this.activeOperations.get(operationId);
    if (operation) {
      this.addToHistory(operation);
      this.activeOperations.delete(operationId);
    }
  }

  /**
   * Add operation to history
   */
  addToHistory(operation) {
    this.operationHistory.unshift(operation);

    // Trim history if too large
    if (this.operationHistory.length > this.maxHistorySize) {
      this.operationHistory = this.operationHistory.slice(0, this.maxHistorySize);
    }
  }

  /**
   * Get status of all operations
   */
  getStatus() {
    return {
      activeOperations: Array.from(this.activeOperations.values()),
      activeLocks: Array.from(this.locks.entries()),
      recentHistory: this.operationHistory.slice(0, 10),
      stats: {
        totalActive: this.activeOperations.size,
        totalLocks: this.locks.size,
        maxConcurrent: this.maxConcurrentOperations,
        historySize: this.operationHistory.length
      }
    };
  }

  /**
   * Wait for all operations to complete
   */
  async waitForAllOperations(timeout = 30000) {
    const startTime = Date.now();

    while (this.activeOperations.size > 0) {
      if (Date.now() - startTime > timeout) {
        throw new Error(`Timeout waiting for operations to complete. ${this.activeOperations.size} operations still active.`);
      }

      // Wait 100ms before checking again
      await new Promise(resolve => setTimeout(resolve, 100));
    }
  }

  /**
   * Force cancel all operations (emergency use only)
   */
  forceCancel() {
    console.warn('🚨 Force canceling all cleanup operations');

    // Clear all timeouts
    for (const timeoutId of this.cleanupTimeouts.values()) {
      clearTimeout(timeoutId);
    }

    // Move all active operations to history as failed
    for (const operation of this.activeOperations.values()) {
      operation.endTime = new Date();
      operation.status = 'failed';
      operation.error = 'Force canceled';
      this.addToHistory(operation);
    }

    // Clear all tracking
    this.activeOperations.clear();
    this.locks.clear();
    this.cleanupTimeouts.clear();
  }
}

// Create singleton instance
const cleanupCoordinator = new CleanupCoordinator();

// Export coordinator and helper functions
export { cleanupCoordinator };

/**
 * Coordinated broker integration cleanup
 */
export const coordinatedBrokerCleanup = async (options = {}) => {
  return cleanupCoordinator.startOperation(
    CLEANUP_TYPES.BROKER_INTEGRATIONS,
    CLEANUP_SCOPES.GLOBAL,
    async () => {
      // Import here to avoid circular dependencies
      const brokerIntegrationRepository = await import('../repositories/brokerIntegrationRepository');
      const repository = brokerIntegrationRepository.default;
      return await repository.cleanupDisconnectedIntegrations();
    },
    {
      description: 'Clean up disconnected broker integrations',
      ...options
    }
  );
};

/**
 * Coordinated engagement data cleanup for specific user
 */
export const coordinatedEngagementCleanup = async (userId, userProfile, options = {}) => {
  return cleanupCoordinator.startOperation(
    CLEANUP_TYPES.ENGAGEMENT_DATA,
    CLEANUP_SCOPES.USER_SPECIFIC,
    async () => {
      // Import here to avoid circular dependencies
      const { deleteEngagementsForUser } = await import('./engagementCleanup');
      return await deleteEngagementsForUser({
        userId,
        email: userProfile?.email,
        username: userProfile?.username
      });
    },
    {
      userId,
      description: `Clean up engagement data for user ${userId}`,
      ...options
    }
  );
};

/**
 * Coordinated full user data cleanup
 */
export const coordinatedUserDataCleanup = async (userId, userProfile, options = {}) => {
  return cleanupCoordinator.startOperation(
    CLEANUP_TYPES.USER_DATA,
    CLEANUP_SCOPES.USER_SPECIFIC,
    async () => {
      // Run both broker and engagement cleanup sequentially for user
      let brokerResult = null;
      let engagementResult = null;

      try {
        // First clean broker integrations
        const brokerIntegrationRepository = await import('../repositories/brokerIntegrationRepository');
        const repository = brokerIntegrationRepository.default;
        brokerResult = await repository.cleanupDisconnectedIntegrations();
      } catch (error) {
        console.warn('Broker cleanup failed during user cleanup:', error);
      }

      try {
        // Then clean engagement data
        const { deleteEngagementsForUser } = await import('./engagementCleanup');
        engagementResult = await deleteEngagementsForUser({
          userId,
          email: userProfile?.email,
          username: userProfile?.username
        });
      } catch (error) {
        console.warn('Engagement cleanup failed during user cleanup:', error);
      }

      return {
        brokerCleanup: brokerResult,
        engagementCleanup: engagementResult,
        totalDeleted: (brokerResult?.deleted || 0) + (engagementResult || 0)
      };
    },
    {
      userId,
      description: `Complete data cleanup for user ${userId}`,
      timeout: 10 * 60 * 1000, // 10 minutes for full user cleanup
      ...options
    }
  );
};

/**
 * Get cleanup coordinator status
 */
export const getCleanupStatus = () => {
  return cleanupCoordinator.getStatus();
};

/**
 * Wait for all cleanup operations to complete
 */
export const waitForCleanupCompletion = (timeout) => {
  return cleanupCoordinator.waitForAllOperations(timeout);
};

// Debug helpers - only in development
if (typeof window !== 'undefined' && process.env.NODE_ENV === 'development') {
  window.cleanupCoordinator = cleanupCoordinator;
  window.getCleanupStatus = getCleanupStatus;
}

export default cleanupCoordinator;